import { TableHeader } from 'components/organisms/table';
import { Suspense, useContext, useEffect, useState } from 'react';
import styles from '../../../../pages/intermediary/intermediary.module.scss';
import { transactionsTableHeaderData } from './transactions-table.function';
import { TransactionsTableRow, Transaction } from './TransactionsTableRow';
import WidgetLoader from '../../../../atoms/widget-loader/WidgetLoader';
import WidgetError from '../../../../atoms/widget-error/WidgetError';
import {
  Icon,
  ICONS_ENUM,
  Table,
  TableBody,
  TableCell
} from '@sanlam-glacier/app-common/components';
import { LatestTransaction } from 'bff';
import { getLatestTransactions } from 'bff/hooks/reporting/LatestTransactionsApi';
import { useQuery } from '@tanstack/react-query';
import { TableProps } from 'modules/dashboard/hooks/TableRenderer';
import { WidgetContext } from 'modules/dashboard/components/organisms/widget/Widget';
import { useSetWidgetQueryStatus } from 'modules/dashboard/types/widgets/widget.hooks';

export const TransactionsTable = ({ data }: TableProps) => {
  const context = useContext(WidgetContext);

  const {
    data: latestTransactionsData,
    isFetching,
    isError,
    refetch,
    isSuccess
  } = useQuery(getLatestTransactions({ clientId: 1 }));

  useEffect(() => {
    if (latestTransactionsData) {
      setTableData(latestTransactionsData as Transaction[]);
    } else {
      setTableData(data as Transaction[]);
    }
  }, [latestTransactionsData]);

  useEffect(() => {
    if (!context) return;
    context.setReloadHandler(() => () => {
      refetch();
    });
  }, []);

  useSetWidgetQueryStatus({ isFetching, isError, isSuccess, context });

  const [tableData, setTableData] = useState<LatestTransaction[]>([]);
  const [pageSize] = useState(5);
  const [page, setPage] = useState(0);
  const totalPages = Math.ceil(tableData.length / 5);

  const getPaginationRange = () => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (page < 3) {
      return [1, 2, 3, '...', totalPages];
    }

    if (page > totalPages - 4) {
      return [1, '...', totalPages - 2, totalPages - 1, totalPages];
    }

    return [1, '...', page, page + 1, page + 2, '...', totalPages];
  };

  const paginatedData = tableData.slice(page * pageSize, page * pageSize + pageSize);

  return (
    <Suspense fallback={<WidgetLoader />}>
      {isFetching ? (
        <WidgetLoader />
      ) : isError ? (
        <WidgetError onRefresh={refetch} isRefreshing={isFetching} />
      ) : (
        <>
          <Table data-testid='transactionsTable'>
            <TableHeader
              headers={transactionsTableHeaderData}
              align={TableCell.ALIGN.LEFT}
              tableHeaderClassName={styles.tableSortHeaderLabel}
            />
            <TableBody data-testid='transactionsTableDetails'>
              {paginatedData.map((row) => (
                <TransactionsTableRow key={row.id} row={row} />
              ))}
            </TableBody>
          </Table>
          <div className={styles.footer}>
            <div className={styles.paginationWrapper}>
              <ul className={styles.pagination}>
                <li
                  className={`${styles.pageItem} ${page === 0 ? styles.disabled : ''}`}
                  onClick={() => page > 0 && setPage(page - 1)}
                >
                  <Icon
                    icon={ICONS_ENUM.NAVIGATION_ARROW_BACK_IOS}
                    data-testid={`transactionsTablePrevButton${page}`}
                    className={styles.arrowIcon}
                  />
                </li>

                {getPaginationRange().map((item, idx) => (
                  <li
                    key={idx}
                    className={`${styles.pageItem} ${
                      item === page + 1 ? styles.active : ''
                    } ${item === '...' ? styles.ellipsis : ''}`}
                    onClick={() => typeof item === 'number' && setPage(item - 1)}
                  >
                    {item}
                  </li>
                ))}

                <li
                  className={`${styles.pageItem} ${page === totalPages - 1 ? styles.disabled : ''}`}
                  onClick={() => page < totalPages - 1 && setPage(page + 1)}
                >
                  <Icon
                    icon={ICONS_ENUM.NAVIGATION_ARROW_FORWARD_IOS}
                    data-testid={`transactionsTableNextButton${page}`}
                    className={styles.arrowIcon}
                  />
                </li>
              </ul>
            </div>
            <button
              className={styles.statementButton}
              data-testid='transactionsTableGoToStatementsButton'
            >
              Go to statements{' '}
              <Icon icon={ICONS_ENUM.NAVIGATION_ARROW_FORWARD} className={styles.arrowIcon} />
            </button>
          </div>
        </>
      )}
    </Suspense>
  );
};
